<template>
  <div class="custom-input-group z-20">
    <v-select
      :items="getSelectItems"
      v-model="selectValue"
      solo
      flat
      dense
      hide-details
      class="select-input"
      label="Select"
      :style="{ width: '150px' }"
      :append-icon="getSelectItems.length === 1 ? '' : undefined"
      :disabled="getSelectItems.length === 1"
    />
    <v-text-field
      v-if="textFieldSelection"
      v-model="formattedInputValue"
      solo
      flat
      dense
      hide-details
      label="Type here for search..."
      placeholder="Type here for search..."
      class="text-input"
      :style="{ width: '700px' }"
      clearable
      @input="debouncedHandleInput"
    />
    <v-autocomplete
      v-else
      v-model="inputValue"
      :items="[...(recentSearches[selectValue] || []), ...filteredSearchSuggestions]"
      :search-input.sync="searchValue"
      solo
      flat
      dense
      hide-details
      class="text-input"
      :style="{ width: '700px' }"
      append-icon=""
      label="Type here for search..."
      placeholder="Type here for search..."
      @update:search-input="debouncedFetchSuggestions"
      @change="handleSelection"
      @click:clear="clearSearchQuery"
      :return-object="true"
      clearable
      no-filter
    >
      <template v-slot:item="data">
        <div class="flex justify-between text-sm">
          <span>{{ data.item.text }}</span>
          <v-icon
            small
            class="ml-2"
            @click.stop="removeSearchTerm(data.item)"
            v-if="recentSearches[selectValue]?.includes(data.item)"
          >
            mdi-close-circle
          </v-icon>
        </div>
      </template>
    </v-autocomplete>
  </div>
</template>

<script>
import debounce from 'lodash.debounce'
import { SelectionTypes, SearchConfig } from '@/utils/workforce/constants'
import { axiosUrl } from '@/utils/workforceApi'

export default {
  props: {
    enableFetch: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      searchValue: '',
      searchSuggestions: [],
      recentSearches: this.getRecentSearches()
    }
  },
  computed: {
    // Current route configuration
    currentRouteConfig() {
      return SearchConfig.getRouteConfig(this.$route.path)
    },

    // Active tab from route query
    activeTab() {
      return this.$route.query.activeTab || ''
    },

    // Available select items based on route and tab
    getSelectItems() {
      return SearchConfig.getSelectItems(this.currentRouteConfig, this.activeTab)
    },

    // Selected filter type
    selectValue: {
      get() {
        return (
          this.$store.getters.getSelectValue ||
          this.getSelectItems[0]?.value ||
          SelectionTypes.EMPLOYEES
        )
      },
      set(value) {
        this.$store.dispatch('updateSelectValue', value)
      }
    },

    // Input value from store
    inputValue: {
      get() {
        return this.$store.getters.getInputValue
      },
      set(value) {
        this.$store.dispatch('updateInputValue', value)
      }
    },

    // Whether to show text field or autocomplete
    textFieldSelection() {
      return (
        !this.enableFetch ||
        ![
          SelectionTypes.EMPLOYEES,
          SelectionTypes.CUSTOMERS,
          SelectionTypes.PARTNERS,
          SelectionTypes.WORKFLOW_TEMPLATES
        ].includes(this.selectValue)
      )
    },

    // Formatted input value for text field
    formattedInputValue: {
      get() {
        if (typeof this.inputValue === 'object' && this.inputValue !== null) {
          return (
            this.inputValue.name ||
            this.inputValue.customerId ||
            this.inputValue.empId ||
            this.inputValue.partnerId ||
            ''
          )
        }
        return this.inputValue
      },
      set(value) {
        this.inputValue = value
      }
    },

    // Filtered search suggestions (excluding recent searches)
    filteredSearchSuggestions() {
      if (!this.selectValue || !this.searchSuggestions.length) return []

      const recentSearchesSet = new Set(
        (this.recentSearches[this.selectValue] || []).map((item) => item.text.toLowerCase())
      )

      return this.searchSuggestions.filter((suggestion) => {
        const suggestionText = suggestion.text.toLowerCase()
        return (
          !recentSearchesSet.has(suggestionText) &&
          !Array.from(recentSearchesSet).some((recent) => this.isSimilar(recent, suggestionText))
        )
      })
    }
  },
  watch: {
    // Reset fields when route path changes
    '$route.path'() {
      this.resetFields()
    },

    // Handle active tab changes
    '$route.query.activeTab'(newTab, oldTab) {
      // Skip if this is initial load
      if (!oldTab && newTab) return

      // Reset fields and clear search when tab changes
      if (newTab !== oldTab) {
        // Special case: If switching to 'map' and we have a preserved search (likely from timeline view), don't clear
        if (
          newTab === 'map' &&
          this.$route.query.search &&
          this.$route.query.search.match(/^[a-f\d]{24}$/i)
        ) {
          return
        }

        this.resetFields()
        this.clearSearchQuery()
      }
    },

    // Watch for inputValue changes to update recent searches
    inputValue: {
      handler(newVal) {
        if (
          newVal &&
          typeof newVal === 'object' &&
          newVal.text &&
          newVal.value &&
          this.selectValue
        ) {
          this.saveSearchTerm(newVal)
        }
      },
      deep: true
    },

    // Synchronize route query parameters with inputValue
    '$route.query': {
      handler(newQuery) {
        this.syncInputValueFromQuery(newQuery)
      },
      immediate: true,
      deep: true
    }
  },
  methods: {
    // Utility methods
    isSimilar(a, b) {
      return a.includes(b) || b.includes(a)
    },

    hasQueryParamsChanged(newParams) {
      const currentQuery = this.$route.query
      return (
        Object.keys(newParams).length !== Object.keys(currentQuery).length ||
        Object.keys(newParams).some((key) => newParams[key] !== currentQuery[key])
      )
    },

    handleNavigationError(err) {
      if (err?.name !== 'NavigationDuplicated') {
        console.error('Navigation error', err)
      }
    },

    updateRoute(queryParams) {
      if (this.hasQueryParamsChanged(queryParams)) {
        this.$router
          .push({ path: this.$route.path, query: queryParams })
          .catch(this.handleNavigationError)
      }
    },

    // Local storage methods
    getRecentSearches() {
      if (typeof window !== 'undefined') {
        return JSON.parse(localStorage.getItem('recentSearches')) || {}
      }
      return {}
    },

    saveRecentSearches(searches) {
      if (typeof window !== 'undefined') {
        localStorage.setItem('recentSearches', JSON.stringify(searches))
        this.recentSearches = searches
      }
    },

    // Main functionality methods
    resetFields() {
      this.searchValue = ''
      this.searchSuggestions = []
      this.$store.dispatch('updateSelectValue', null)
      this.$store.dispatch('updateInputValue', null)
    },

    // Synchronize inputValue from route query parameters
    async syncInputValueFromQuery(query) {
      if (!this.currentRouteConfig || !query) return

      // Auto-set selectValue for CaseCustomer tab
      if (query.activeTab === 'CaseCustomer') {
        this.$store.dispatch('updateSelectValue', SelectionTypes.CUSTOMERS)
      }

      // Use nextTick to ensure selectValue is updated before checking customer search format
      this.$nextTick(async () => {
        // For CaseCustomer tab or customer routes, use 'search' parameter
        if (query.activeTab === 'CaseCustomer' && query.search) {
          // ... (existing logic)
          if (this.enableFetch) {
            await this.fetchAndSetCustomerFromSearch(query.search)
          } else {
            this.$store.dispatch('updateInputValue', query.search)
          }
          return
        }

        const isCustomerSearchFormat = SearchConfig.isCustomerSearchFormat(
          this.currentRouteConfig,
          this.selectValue || SelectionTypes.CUSTOMERS,
          this.activeTab,
          this.$route.path
        )

        if (isCustomerSearchFormat && query.search) {
          // ... (existing logic)
          if (this.enableFetch && this.selectValue === SelectionTypes.CUSTOMERS) {
            await this.fetchAndSetCustomerFromSearch(query.search)
          } else {
            this.$store.dispatch('updateInputValue', query.search)
          }
          return
        }

        // Standard query key processing
        const queryKey = SearchConfig.getQueryKey(
          this.currentRouteConfig,
          this.selectValue,
          this.activeTab
        )

        if (queryKey && query[queryKey]) {
          const queryValue = query[queryKey]

          // Check if current input value is an object (user/customer details) and matches the query ID
          // This prevents overwriting the detailed object with just the ID string from query
          const currentInput = this.$store.getters.getInputValue
          const inputData = currentInput?.value || currentInput

          const isSameId =
            inputData &&
            typeof inputData === 'object' &&
            (inputData.empId === queryValue ||
              inputData.customerId === queryValue ||
              inputData.partnerId === queryValue)

          if (!isSameId) {
            this.$store.dispatch('updateInputValue', queryValue)
          }
        }
      })
    },

    // Fetch customer data by search term and set as selection
    async fetchAndSetCustomerFromSearch(searchTerm) {
      try {
        const response = await this.$axios.get(axiosUrl.customers.getCustomers, {
          params: {
            page: 1,
            limit: 10,
            search: searchTerm
          }
        })

        const customers = response.data?.customers || []

        // Find exact match first, then partial match
        let matchedCustomer = customers.find((customer) => {
          const customerName = SearchConfig.formatCustomerName(customer)
          return customerName.toLowerCase() === searchTerm.toLowerCase()
        })

        // If no exact match, try partial match
        if (!matchedCustomer && customers.length > 0) {
          matchedCustomer = customers.find((customer) => {
            const customerName = SearchConfig.formatCustomerName(customer)
            return (
              customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
              searchTerm.toLowerCase().includes(customerName.toLowerCase())
            )
          })
        }

        // If still no match, use the first customer if available
        if (!matchedCustomer && customers.length > 0) {
          matchedCustomer = customers[0]
        }

        if (matchedCustomer) {
          // Create selection object in the same format as fetchCustomerSuggestions
          const customerSelection = {
            text: `${SearchConfig.formatCustomerName(matchedCustomer)} - ${
              matchedCustomer?.crn ?? 'N/A'
            }`,
            value: {
              name: SearchConfig.formatCustomerName(matchedCustomer),
              customerId: matchedCustomer?._id ?? 'N/A',
              crn: matchedCustomer?.crn ?? 'N/A'
            }
          }

          // Set the selection object as inputValue
          this.$store.dispatch('updateInputValue', customerSelection)

          // Save the customer selection to recent searches
          this.saveSearchTerm(customerSelection)
        } else {
          // If no customer found, just set the search term as string
          this.$store.dispatch('updateInputValue', searchTerm)
        }
      } catch (error) {
        console.error('Error fetching customer for search term:', error)
        // Fallback to setting the search term as string
        this.$store.dispatch('updateInputValue', searchTerm)
      }
    },
    clearSearchQuery() {
      this.$store.dispatch('updateInputValue', '')

      if (!this.currentRouteConfig.queryKeys) return

      const queryParams = { ...this.$route.query }

      // Remove all query parameters defined for the current route
      Object.values(this.currentRouteConfig.queryKeys).forEach((keys) => {
        if (typeof keys === 'object') {
          Object.values(keys).forEach((key) => delete queryParams[key])
        } else {
          delete queryParams[keys]
        }
      })

      this.updateRoute(queryParams)
    },
    // Search suggestions fetching
    fetchSuggestions() {
      if (
        !this.enableFetch ||
        !this.searchValue ||
        (this.inputValue && typeof this.inputValue === 'object')
      ) {
        return
      }

      const fetchMethods = {
        [SelectionTypes.EMPLOYEES]: this.fetchEmployeeSuggestions,
        [SelectionTypes.TEAMS]: this.fetchTeamSuggestions,
        [SelectionTypes.CUSTOMERS]: this.fetchCustomerSuggestions,
        [SelectionTypes.PARTNERS]: this.fetchPartnerSuggestions,
        [SelectionTypes.WORKFLOW_TEMPLATES]: this.fetchWorkflowTemplateSuggestions
      }

      const fetchMethod = fetchMethods[this.selectValue]
      if (fetchMethod) {
        fetchMethod()
      }
    },

    async fetchEmployeeSuggestions() {
      try {
        const response = await this.$axios.get(axiosUrl.employees.getEmployees, {
          params: { search: this.searchValue }
        })

        this.searchSuggestions =
          response.data.users?.map((user) => ({
            text: `${SearchConfig.formatUserName(user)} / ${
              user.employmentDetails?.empId || user.employeeId || 'N/A'
            }`,
            value: {
              name: user.fullName,
              empId: user._id,
              empNumber: user.mobile
            }
          })) || []
      } catch (error) {
        console.error('Error fetching employee suggestions:', error)
      }
    },

    async fetchTeamSuggestions() {
      try {
        const response = await this.$axios.get('/collection/cases', {
          params: { search: this.searchValue }
        })

        this.searchSuggestions =
          response.data.cases?.map((el) => ({
            text: el.customer
              ? `${el.caseNo} - ${SearchConfig.formatCustomerName(el.customer)} - ${
                  el.customer?.crn ?? 'N/A'
                }`
              : el.caseNo,
            value: {
              cNo: el.caseNo,
              sCName: el.caseType ?? 'Unknown'
            }
          })) || []
      } catch (error) {
        console.error('Error fetching team suggestions:', error)
      }
    },

    async fetchCustomerSuggestions() {
      try {
        const response = await this.$axios.get(axiosUrl.customers.getCustomers, {
          params: {
            page: 1,
            limit: 5,
            search: this.searchValue
          }
        })

        this.searchSuggestions =
          response.data?.customers?.map((customer) => ({
            text: `${SearchConfig.formatCustomerName(customer)} - ${customer?.crn ?? 'N/A'}`,
            value: {
              name: SearchConfig.formatCustomerName(customer),
              customerId: customer?._id ?? 'N/A',
              crn: customer?.crn ?? 'N/A'
            }
          })) || []
      } catch (error) {
        console.error('Error fetching customer suggestions:', error)
      }
    },

    async fetchPartnerSuggestions() {
      try {
        const response = await this.$axios.get(axiosUrl.partners.getPartners, {
          params: {
            page: 1,
            limit: 5,
            search: this.searchValue
          }
        })

        this.searchSuggestions =
          response.data?.partners?.map((partner) => ({
            text: `${partner.partnerName || 'Unknown Partner'} - ${partner.partnerStatus || 'N/A'}`,
            value: {
              name: partner.partnerName || 'Unknown Partner',
              partnerId: partner._id || 'N/A',
              partnerStatus: partner.partnerStatus || 'N/A'
            }
          })) || []
      } catch (error) {
        console.error('Error fetching partner suggestions:', error)
      }
    },

    // Input handling
    handleInput(inputValue) {
      const queryKey = SearchConfig.getQueryKey(
        this.currentRouteConfig,
        this.selectValue,
        this.activeTab
      )
      if (!queryKey) return

      const queryParams = { ...this.$route.query }
      inputValue ? (queryParams[queryKey] = inputValue) : delete queryParams[queryKey]

      this.updateRoute(queryParams)
    },

    // Selection handling
    handleSelection(selectedItem) {
      const { value } = selectedItem || {}
      const queryParams = { ...this.$route.query }

      if (value) {
        this.setQueryParam(queryParams, value)
      } else {
        this.clearQueryParam(queryParams)
      }

      this.updateRoute(queryParams)

      if (selectedItem) {
        this.saveSearchTerm(selectedItem)
      }
    },

    setQueryParam(queryParams, value) {
      const isCustomerSearchFormat = SearchConfig.isCustomerSearchFormat(
        this.currentRouteConfig,
        this.selectValue,
        this.activeTab,
        this.$route.path
      )

      if (isCustomerSearchFormat) {
        queryParams.search = value.customerFullName || value.name || value.firstName || ''
        delete queryParams.customer
      } else {
        const queryKey = SearchConfig.getQueryKey(
          this.currentRouteConfig,
          this.selectValue,
          this.activeTab
        )
        if (queryKey) {
          // Determine which value to use based on selection type and tab
          if (this.activeTab === 'CallHistory') {
            queryParams[queryKey] = value.empNumber
          } else if (this.selectValue === SelectionTypes.CUSTOMERS) {
            queryParams[queryKey] = value.customerId
          } else if (this.selectValue === SelectionTypes.PARTNERS) {
            queryParams[queryKey] = value.partnerId
          } else {
            queryParams[queryKey] = value.empId
          }
        }
      }
    },

    clearQueryParam(queryParams) {
      const isCustomerSearchFormat = SearchConfig.isCustomerSearchFormat(
        this.currentRouteConfig,
        this.selectValue,
        this.activeTab,
        this.$route.path
      )

      if (isCustomerSearchFormat) {
        delete queryParams.search
      } else {
        const queryKey = SearchConfig.getQueryKey(
          this.currentRouteConfig,
          this.selectValue,
          this.activeTab
        )
        if (queryKey) {
          delete queryParams[queryKey]
        }
      }
    },
    // Recent search management
    saveSearchTerm(term) {
      const searches = this.getRecentSearches()

      if (!searches[this.selectValue]) {
        searches[this.selectValue] = []
      }

      // Remove existing item if found
      const uniqueId = SearchConfig.getUniqueId(term, this.selectValue)
      const existingIndex = searches[this.selectValue].findIndex(
        (item) => SearchConfig.getUniqueId(item, this.selectValue) === uniqueId
      )

      if (existingIndex !== -1) {
        searches[this.selectValue].splice(existingIndex, 1)
      }

      // Add to beginning and limit to 5 items
      searches[this.selectValue].unshift(term)
      if (searches[this.selectValue].length > 5) {
        searches[this.selectValue].pop()
      }

      this.saveRecentSearches(searches)
    },

    removeSearchTerm(item) {
      const searches = this.getRecentSearches()

      if (searches[this.selectValue]) {
        const uniqueId = SearchConfig.getUniqueId(item, this.selectValue)
        searches[this.selectValue] = searches[this.selectValue].filter(
          (search) => SearchConfig.getUniqueId(search, this.selectValue) !== uniqueId
        )
        this.saveRecentSearches(searches)
      }
    }
  },

  created() {
    // Setup debounced methods
    this.debouncedHandleInput = debounce(this.handleInput, 500)
    this.debouncedFetchSuggestions = debounce(this.fetchSuggestions, 500)
  },

  beforeDestroy() {
    // Clean up store state
    this.$store.dispatch('updateSelectValue', null)
    this.$store.dispatch('updateInputValue', null)
  }
}
</script>

<style scoped>
.custom-input-group {
  display: flex;
  align-items: center;
  padding: 2px;
  background-color: white;
}

.custom-input-group .v-select .v-input__control,
.custom-input-group .v-autocomplete .v-input__control {
  background-color: white !important;
  min-height: 40px !important;
  height: 40px !important;
}

.select-input {
  flex-grow: 1;
  font-size: smaller;
  border-right: 1px solid #ccc;
  border-top-right-radius: 0px;
  border-bottom-right-radius: 0px;
}

.text-input {
  flex-grow: 2;
  margin-left: 2px;

  border-top-left-radius: 0px;
  border-bottom-left-radius: 0px;
}
</style>
